import { Injectable } from '@angular/core';
import { Address } from '@checkout/const';
import { DeliveryAddressFacade, TimeSlotsFacade } from '@checkout/data-access';
import { DeliveryZone } from '@delivery/const';
import { DeliveryZonesService } from '@delivery/feature';
import { GeocodeService, YmapsService } from '@yandex-maps/feature';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DeliveryAddressService } from './delivery-address.service';

@Injectable({
  providedIn: 'root',
})
export class ZoneFinderService {
  constructor(
    private geocodeService: GeocodeService,
    private timeSlotsFacade: TimeSlotsFacade,
    private ymapsService: YmapsService,
    private deliveryAddressFacade: DeliveryAddressFacade,
    private deliveryAddressService: DeliveryAddressService,
    private deliveryZonesService: DeliveryZonesService,
  ) {}

  getZone(id: string): Observable<DeliveryZone | null> {
    return this.deliveryAddressFacade.getAddress(id).pipe(
      switchMap((address: Address) => {
        if (!address) {
          this.timeSlotsFacade.resetTimeSlots();

          return of(null);
        }

        if (!!address.point.latitude && !!address.point.longitude) {
          return this.findZone([address.point.latitude, address.point.longitude]).pipe(take(1));
        }

        return this.geocodeService.getGeoObject(address.formattedAddress).pipe(
          map((geoObject) => {
            if (!geoObject) {
              this.timeSlotsFacade.resetTimeSlots();

              return null;
            } else {
              return geoObject?.geometry.getCoordinates();
            }
          }),
          tap((coords) => {
            // сохраняем добытые в яндексе координаты в адрес,
            // а то хули он без координат
            this.refreshAddressCoordsIfNeeded(address, coords);
          }),
          switchMap((coords) => this.findZone(coords)),
          take(1),
        );
      }),
    );
  }

  refreshAddressCoordsIfNeeded(address: Address, coords: ymaps.Coordinate): void {
    const newAddress = Object.assign({}, address);
    newAddress.point = { latitude: coords[0], longitude: coords[1] };
    this.deliveryAddressService.editSavedAddress(newAddress);
  }

  findZone(center: ymaps.Coordinate): Observable<DeliveryZone | null> {
    return this.ymapsService.ymaps$.pipe(
      switchMap(() => combineLatest([this.deliveryZonesService.deliveryZones$, this.ymapsService.map$])),
      filter(([zones, mapInstance]) => {
        return Boolean(zones && mapInstance);
      }),
      take(1),
      map(([zones, mapInstance]) => {
        return (
          zones.find((zone: DeliveryZone) => {
            const polygon = new ymaps.geometry.Polygon(zone.polygon.coordinates);
            polygon.options.setParent(mapInstance.options);
            polygon.setMap(mapInstance);

            return polygon.contains(center);
          }) || null
        );
      }),
    );
  }
}
